/**
 * 웹 ERP 사용 기록기. 사람(과 AI 도우미)이 화면에서 한 일을 사건으로 모아 미들서버
 * (`POST /events`, `ErpService/events.go`)에 보낸다.
 *
 * 2026-09-29. 쓰임은 셋이다 — CS 문의 때 그 사람이 무엇을 했는지 보기, 버그를 DOM
 * 드라이버로 다시 실행하기, 에이전트가 사람의 실제 업무 방식을 배우기. 그래서 **값은
 * 최대한 다 남긴다**(사용자 결정). 예외는 비밀번호·자격증명뿐이다.
 *
 * **화면을 느리게 하거나 깨뜨리면 안 된다.** 그래서 —
 * - 리스너는 문서 수준 캡처 단계에 passive 로 하나씩만 건다. 화면 코드는 건드리지 않는다.
 *   예외는 부품이 보내는 작은 신호(`app/utils/usageSignal.ts`) — 표의 확정된 칸 변경과
 *   팝업 검색·선택. DOM 만으로는 안 보이는 것들이다.
 * - 잡는 코드는 전부 try/catch 안이다. 기록이 실패해도 클릭은 그대로 간다.
 * - 화면 상태 비교는 사람이 뭔가 만진 뒤에만, 300ms 모아서 한 번, 스칼라만 한다.
 * - 보내기는 5초마다 또는 50건이 차면 한 번. 실패하면 버린다 — 다시 보내지 않는다.
 *
 * 2판(같은 날 저녁, 첫 배포 자료 2,983건을 본 뒤) —
 * - 칸 사건마다 원인(`extra.cause`)과 상태 전체 경로(`extra.path`)를 싣는다. 사람이 확정한
 *   값(`input`·`popup`)만 `field` 로 남기고, 화면이 채운 값은 원인과 함께 `state` 한 줄로 묶는다.
 * - 표(`TableLayout`)의 체크·칸 수정을 부품 신호로 받아 행 업무 키와 함께 `cell` 로 남긴다.
 * - 팝업을 열고·찾고·고르고·닫은 것을 `lookupId` 로 이어 `lookup` 으로 남긴다.
 * - 모든 줄에 세션 순번(`extra.seq`)과 기록기 판(`extra.v`)을 싣는다.
 *
 * 앱 소유 파일이다. `driver/`·`runtime/` 은 다른 리포에서 동기화되므로 거기 두지 않는다.
 */

import {
  Cause, CellCommit, EventBuffer, EventInput, Gesture, KEEPALIVE_BYTES, MAX_BATCH, NORMAL_BYTES, Scalar, Trigger,
  actorOf, batchBody, buildEvent, causeOfTrigger, cellKey, cellSource, diffState, errorGate, fieldCause,
  flattenState, isCheckValue, isChecked, isSecretKey, labelFieldKey, leafKey, lookupItem, lookupValues, mergeCell,
  newSessionId, pairedNameOf, rowIdentity, rowLabel, seqStart, settledCell, shouldFlushNow, toScalar,
} from './recorderCore';

export type RecorderCaller = () => { custcd: string; perid: string; token: string } | null;

const EVENTS = '/events';
const SESSION_KEY = 'elmanRecorderSession';
const SEQ_KEY = 'elmanRecorderSeq';
/** `localStorage.elmanRecorder = 'off'` 이면 기록하지 않는다. 문제가 생긴 PC 에서 끄는 손잡이다. */
const OFF_KEY = 'elmanRecorder';
const TICK_MS = 1000;
const FLUSH_TICKS = 5;
const DIFF_DEBOUNCE_MS = 300;
/** 조회를 누르면 결과는 서버를 다녀와서 온다. 그동안은 비교를 미룬다 — 최대 이만큼. */
const DIFF_BUSY_RETRY_MS = 500;
const DIFF_BUSY_MAX_TRIES = 30;
/** 이름 모를 상태 변화를 한 사건에 싣는 최대 키 수와 값 길이. */
const STATE_MAX_KEYS = 30;
const STATE_MAX_VALUE = 300;
/** 같은 칸의 연속 변경(글자마다 부르는 칸)을 모으는 시간. 이만큼 조용하면 한 줄로 낸다. */
const CELL_SETTLE_MS = 800;
/** 체크칸을 누르고 이만큼 뒤에 켜짐이 바뀌었는지 본다(체크칸은 네트워크 중이면 300ms 씩 미룬다). */
const CHECK_VERIFY_MS = 1200;
/** 사람이 만진 칸의 기억. 이보다 오래된 손길은 지금 변화의 원인으로 안 본다. */
const TOUCH_TTL_MS = 30000;
/** 팝업을 고른(닫은) 뒤 이만큼 안에 바뀐 칸은 팝업 때문으로 본다. */
const LOOKUP_TTL_MS = 3000;

type Bridge = {
  currentPath?: () => string | null;
  pageState?: () => Record<string, unknown> | null;
  openTabs?: () => { path: string; name: string }[];
  busy?: () => boolean;
};

// `__elmanAgent`·`__elmanAgentBusy` 는 앱이 창에 거는 약속된 이름이다(createStore.ts).
function bridge(): Bridge | null {
  // eslint-disable-next-line no-underscore-dangle
  return (window as unknown as { __elmanAgent?: Bridge }).__elmanAgent ?? null;
}

function agentBusy(): (() => boolean) | undefined {
  // eslint-disable-next-line no-underscore-dangle
  return (window as unknown as { __elmanAgentBusy?: () => boolean }).__elmanAgentBusy;
}

/** client.ts 와 같은 미들서버. 앱과 출처가 다르다. */
function serviceHost(): string {
  return String(process.env.REACT_APP_API_SERVICE_HOST ?? '').replace(/\/$/, '');
}

function currentRoute(): string {
  try {
    return bridge()?.currentPath?.() || window.location.pathname || '/';
  } catch {
    return '/';
  }
}

function sessionId(): string {
  // 같은 탭에서 새로고침해도 이어 보이게 sessionStorage 에 둔다. 막혀 있으면 이번 로드만.
  try {
    const kept = window.sessionStorage.getItem(SESSION_KEY);
    if (kept) return kept;
    const made = newSessionId();
    window.sessionStorage.setItem(SESSION_KEY, made);
    return made;
  } catch {
    return newSessionId();
  }
}

function keptSeq(): string | null {
  try {
    return window.sessionStorage.getItem(SEQ_KEY);
  } catch {
    return null;
  }
}

function switchedOff(): boolean {
  try {
    return window.localStorage.getItem(OFF_KEY) === 'off';
  } catch {
    return false;
  }
}

function text(node: Element | null | undefined, max = 200): string {
  return rowLabel(node?.textContent ?? '', max);
}

function isPasswordInput(node: Element | null): boolean {
  return node instanceof HTMLInputElement && node.type === 'password';
}

/** 화면 위 조건 칸 앵커. 표 안의 칸(`data-el-row` 밑)은 조건 칸이 아니다. */
function fieldAnchor(key: string): HTMLElement | null {
  try {
    const all = document.querySelectorAll<HTMLElement>(`[data-el-field="${CSS.escape(key)}"]`);
    for (let i = 0; i < all.length; i += 1) {
      if (!all[i].closest('[data-el-row]')) return all[i];
    }
  } catch {
    // 아래로
  }
  return null;
}

/** 앵커가 지금 보여 주는 값. 입력칸이 아니면 모른다(undefined). */
function anchorValue(anchor: HTMLElement): string | undefined {
  const input = anchor.matches('input, select, textarea') ? anchor : anchor.querySelector('input, select, textarea');
  if (!input || isPasswordInput(input)) return undefined;
  return valueOf(input);
}

function hasGridAnchor(key: string): boolean {
  try {
    return Boolean(document.querySelector(`[data-el-grid="${CSS.escape(key)}"]`));
  } catch {
    return false;
  }
}

/** 팝업 창(ModalView 의 내용, `role=dialog`). 알림창(sweetalert)은 빼고. */
function dialogOf(node: Element): Element | null {
  const dialog = node.closest('[role=dialog]');
  if (!dialog || dialog.closest('.swal2-container')) return null;
  return dialog;
}

/** 행이 속한 표와 행 번호, 칸. 표 안이 아니면 null. */
function cellPlace(node: Element): { grid: string; row: string; column: string; rowEl: Element } | null {
  const cell = node.closest('[data-el-cell]');
  const row = node.closest('[data-el-row]');
  if (!cell || !row) return null;
  return {
    grid: row.closest('[data-el-grid]')?.getAttribute('data-el-grid') ?? '',
    row: row.getAttribute('data-el-row') ?? '',
    column: cell.getAttribute('data-el-cell') ?? '',
    rowEl: row,
  };
}

/**
 * 칸을 감싼 라벨(`LabelBox`)의 제목 글자. 클래스 이름이 모듈로 섞여도 `LabelBox-module__` 는 남는다.
 */
function labelTextOf(node: Element): string {
  const box = node.closest('[class*="LabelBox-module__container"]');
  const title = box?.querySelector('[class*="LabelBox-module__title"]');
  return title ? String(title.textContent ?? '') : '';
}

/**
 * 조건 칸 앵커(표 밖)의 이름. **팝업 창 안에서 앵커가 없으면** 라벨 글자로 짓는다(`label:점검일자`) —
 * 모달 안 입력은 앵커도 화면 상태도 없는 일이 많아 1판 기록에 모달을 여닫은 것만 남았다(2026-09-30).
 * 화면 본문은 그러지 않는다 — 그 칸들은 상태 비교가 이미 잡는다.
 */
function fieldKeyOf(node: Element): string | null {
  const field = node.closest('[data-el-field]');
  if (field) {
    if (field.closest('[data-el-row]')) return null;
    const key = field.getAttribute('data-el-field');
    return key && !isSecretKey(key) ? key : null;
  }
  if (node.closest('[data-el-row]') || !dialogOf(node)) return null;
  return labelFieldKey(labelTextOf(node));
}

type Lookup = {
  id: string;
  popup: string;
  /** 팝업을 연 칸(앵커 이름). 모르면 undefined. */
  field?: string;
  /** 칸에 쳐 두었던 글자 — 팝업을 열 때 검색어로 넘어간다. */
  typed: string;
  /** 팝업 안에서 마지막으로 찾은 검색어. */
  query: string;
  item?: Record<string, string>;
  values: string[];
  /** 고르거나 닫은 시각. 그 뒤 잠깐 동안 바뀐 칸을 이 팝업 때문으로 본다. */
  doneAt?: number;
  cancelled?: boolean;
  /** 앱 스토어 신호 없이 DOM 으로 짐작한 팝업(모달 안 행을 누르고 창이 닫혔다). */
  dialog?: Element;
};

let installed = false;

export function installRecorder(caller: RecorderCaller): void {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  const buffer = new EventBuffer(2000);
  const errors = errorGate();
  const session = sessionId();
  let seq = seqStart(keptSeq());
  let active = false;
  let inFlight = false;
  let ticks = 0;
  let lastRoute = '';
  let baseline: Record<string, Scalar> = {};
  let diffTimer: ReturnType<typeof setTimeout> | null = null;
  let diffTries = 0;
  /**
   * 마지막으로 사람이 한 일. 사람이 직접 만지지 않은 칸이 바뀌었으면 이것이 원인이다 —
   * 목록 행을 누르면 상세 칸이 통째로 바뀌고, 조회·신규를 누르면 칸이 채워지거나 비워진다.
   */
  let trigger: Trigger = { kind: 'none' };
  /** 사람이 만진 조건 칸(앵커 이름)과 그 시각. 행·단추를 누르면 비운다 — 그 뒤 변화는 그 단추 때문이다. */
  let touchedFields: Record<string, number> = {};
  /** 마지막으로 만진 팝업 칸. 팝업을 연 칸을 알아내는 데 쓴다. */
  let lastSearchField: { key: string; at: number } | null = null;
  /** 화면을 연 뒤 아직 아무도 안 만졌다. 그동안은 화면이 채우는 기본값을 기준에 흡수한다. */
  let pristine = true;
  /** 경로별 마지막으로 남긴 값. 같은 값을 두 번 남기지 않게. */
  const lastField: Record<string, string> = {};
  /** 앵커 이름별 마지막으로 남긴 값. DOM 의 change 와 상태 비교가 겹치지 않게. */
  const lastLeaf: Record<string, string> = {};
  /** 팝업 칸에 사람이 친 글자. 팝업이 코드를 넣고 나면 친 것은 사라지므로 따로 들고 있다. */
  const typed: Record<string, string> = {};
  /** 상태에서 못 찾은 칸의 DOM 값. 상태 비교 때 한 번 더 본다. */
  const domOnly: Record<string, string> = {};
  const lastRow: Record<string, string> = {};
  let lookup: Lookup | null = null;
  let lookupCount = 0;
  let gesture: Gesture | null = null;
  /** 아직 안 낸 표 칸 변경. 같은 칸은 합친다. */
  let pendingCells: Record<string, { commit: CellCommit; route: string; actor: 'human' | 'agent'; ts: number }> = {};
  let cellTimer: ReturnType<typeof setTimeout> | null = null;
  /** 칸별로 부품 신호가 온 시각 — 체크칸 DOM 확인과 겹치지 않게. */
  const hookAt: Record<string, number> = {};

  const refreshActive = () => {
    let who = null;
    try {
      who = caller();
    } catch {
      who = null;
    }
    const next = Boolean(who) && !switchedOff();
    if (!next && active) {
      buffer.clear();
      pendingCells = {};
    }
    active = next;
  };

  const nextSeq = (): number => {
    const one = seq;
    seq += 1;
    try {
      window.sessionStorage.setItem(SEQ_KEY, String(one));
    } catch {
      // 순번은 이번 로드 안에서만이라도 늘어난다.
    }
    return one;
  };

  /** 버퍼에 한 줄. 순번은 **넣는 순서**다 — 시각(ts)이 거꾸로여도 순번으로 인과를 편다. */
  const pushEvent = (input: EventInput, context: { route: string; actor: 'human' | 'agent'; now: number }) => {
    buffer.push(buildEvent(input, { ...context, seq: nextSeq() }));
    if (shouldFlushNow(buffer.size)) setTimeout(flush, 0);
  };

  const record = (input: EventInput) => {
    if (!active) return;
    try {
      checkRoute();
      pushEvent(input, { route: currentRoute(), actor: actorOf(agentBusy()), now: Date.now() });
    } catch {
      // 기록 실패는 조용히 버린다.
    }
  };

  /**
   * 화면이 바뀌었으면 `open` 을 남기고 상태 기준을 새로 잡는다. 화면이 열릴 때 채워지는
   * 콤보 목록·기본값은 사람이 한 일이 아니므로 비교 기준에 넣고 사건으로는 안 남긴다.
   */
  const checkRoute = () => {
    const route = currentRoute();
    if (route === lastRoute) return;
    // 앞 화면에서 모아 둔 칸 변경은 앞 화면 것으로 먼저 낸다.
    flushCells();
    lastRoute = route;
    let name = '';
    try {
      name = bridge()?.openTabs?.()?.find((tab) => tab.path === route)?.name ?? '';
    } catch {
      name = '';
    }
    pushEvent({ kind: 'open', target: name, value: route }, { route, actor: actorOf(agentBusy()), now: Date.now() });
    // 칸 이름은 화면마다 겹친다(cltcd 등). 앞 화면의 기억을 끌고 가지 않는다.
    [lastField, lastLeaf, typed, domOnly, lastRow, hookAt].forEach((memo) => {
      Object.keys(memo).forEach((key) => { Reflect.deleteProperty(memo, key); });
    });
    touchedFields = {};
    trigger = { kind: 'none' };
    pristine = true;
    baseline = snapshot();
  };

  const snapshot = (): Record<string, Scalar> => {
    try {
      return flattenState(bridge()?.pageState?.());
    } catch {
      return {};
    }
  };

  const touchField = (key: string | null, now: number) => {
    if (!key) return;
    touchedFields[key] = now;
    const anchor = fieldAnchor(key);
    if (anchor && (anchor.hasAttribute('data-el-search') || anchor.querySelector('[data-el-search]'))) {
      lastSearchField = { key, at: now };
    }
  };

  /**
   * 사람이 뭔가 만졌다. 300ms 모아서 상태를 한 번 비교한다. `next` 를 주면 원인을 바꾼다 —
   * 행·단추·탭을 누르면 앞서 만진 칸의 기억은 지운다(그 뒤 변화는 그 누름 때문이다).
   */
  const touched = (next?: Trigger) => {
    if (!active) return;
    if (next) {
      trigger = next;
      if (next.kind !== 'input' && next.kind !== 'popup') touchedFields = {};
    }
    pristine = false;
    diffTries = 0;
    if (diffTimer) clearTimeout(diffTimer);
    diffTimer = setTimeout(runDiff, DIFF_DEBOUNCE_MS);
  };

  /** 지금 칸 변화에 걸리는 팝업 — 고르거나 닫은 지 얼마 안 된 것. DOM 짐작 팝업은 창이 닫혔을 때만. */
  const settledLookup = (now: number): Lookup | null => {
    if (!lookup) return null;
    if (lookup.dialog && lookup.doneAt === undefined && !lookup.dialog.isConnected) {
      lookup.doneAt = now;
      // 코드·이름 없이 줄 글자만 있으면 고른 것이 아니라 창이 닫힌 것이다 — 날짜 줄·「완료 | …」 같은
      // 대화상자가 2,327건(고르기의 42%) 섞여 별칭 학습을 더럽혔다(2차 연구 B-2). 따로 적는다.
      const named = Boolean(lookup.item && Object.entries(lookup.item)
        .some(([key, value]) => key !== 'label' && String(value ?? '').trim()));
      record({
        kind: 'lookup',
        target: lookup.popup,
        value: lookup.query || lookup.typed,
        extra: {
          step: named ? 'pick' : 'dialog-close',
          lookupId: lookup.id,
          field: lookup.field,
          typed: lookup.typed,
          picked: lookup.item,
          guessed: true,
        },
      });
    }
    if (lookup.doneAt === undefined || now - lookup.doneAt > LOOKUP_TTL_MS) return null;
    return lookup;
  };

  const runDiff = () => {
    diffTimer = null;
    if (!active) return;
    try {
      checkRoute();
      // 서버를 다녀오는 중이면 결과가 아직 안 왔다. 조금 뒤에 다시 본다.
      if (bridge()?.busy?.() && diffTries < DIFF_BUSY_MAX_TRIES) {
        diffTries += 1;
        diffTimer = setTimeout(runDiff, DIFF_BUSY_RETRY_MS);
        return;
      }
      const now = snapshot();
      // 화면을 막 열었다(메뉴 클릭의 뒤끝). 화면이 스스로 채운 것은 기준으로만 삼는다.
      if (pristine) {
        baseline = now;
        return;
      }
      const at = Date.now();
      const changes = diffState(baseline, now);
      baseline = now;
      const picked = settledLookup(at);
      const fallback: Cause = picked ? 'popup' : causeOfTrigger(trigger);
      // 같은 끝 이름이 이번에 몇 경로에서 바뀌었나.
      const leafCount: Record<string, number> = {};
      changes.forEach((change) => {
        if (!change.key.startsWith('#')) leafCount[leafKey(change.key)] = (leafCount[leafKey(change.key)] ?? 0) + 1;
      });
      const filled: Record<string, unknown> = {};
      let filledCount = 0;
      let usedLookup = false;
      /** 이번 비교에서 사람 것으로 남긴 끝 이름. 같은 값을 받아 적은 다른 객체(`focused.cltcd`)는 따라 채운 것이다. */
      const userLeaf: Record<string, true> = {};
      // 얕은 경로부터 본다 — 화면 칸은 대개 맨 위(`cltcd`)나 한 단계(`data.cltcd`)에 있다.
      const depth = (path: string) => path.split('.').length;
      const ordered = changes.slice().sort((a, b) => depth(a.key) - depth(b.key));
      ordered.forEach((change) => {
        const { key } = change;
        if (key.startsWith('#')) {
          // 배열은 길이만 본다. 표 앵커가 없는 화면이 절반이라 앵커 없는 배열도 남긴다.
          const grid = key.slice(1);
          if (change.after === undefined) return;
          const anchored = hasGridAnchor(grid);
          // 처음 생긴 배열은 대개 콤보 선택지 목록이다. 표 앵커가 있을 때만 남긴다.
          if (change.before === undefined && !anchored) return;
          delete lastRow[grid];
          record({
            kind: 'rows',
            target: grid,
            value: change.after,
            extra: { before: change.before ?? null, grid: anchored, cause: fallback },
          });
          return;
        }
        const leaf = leafKey(key);
        if (isSecretKey(leaf)) return;
        const value = change.after === undefined || change.after === null ? '' : String(change.after);
        const anchor = fieldAnchor(leaf);
        if (anchor && (isPasswordInput(anchor) || anchor.querySelector('input[type=password]'))) return;
        if (anchor && lastField[key] === value) return;
        const touchedAt = touchedFields[leaf];
        const judged: Cause = anchor ? fieldCause({
          leaf,
          value,
          touched: touchedAt !== undefined && at - touchedAt < TOUCH_TTL_MS,
          rivals: (leafCount[leaf] ?? 1) - 1,
          domValue: anchorValue(anchor),
          lookup: picked ? { field: picked.field, values: picked.values } : null,
          trigger,
        }) : fallback;
        const cause: Cause = userLeaf[leaf] && (judged === 'input' || judged === 'popup') ? 'auto' : judged;
        if (anchor && (cause === 'input' || cause === 'popup')) {
          userLeaf[leaf] = true;
          // 손길은 이 값으로 확정됐다. 뒤에 화면이 같은 칸을 또 바꾸면 사람 것이 아니다.
          delete touchedFields[leaf];
          lastField[key] = value;
          lastLeaf[leaf] = value;
          delete domOnly[leaf];
          const search = anchor.hasAttribute('data-el-search') || Boolean(anchor.querySelector('[data-el-search]'));
          const extra: Record<string, unknown> = { before: change.before ?? null, path: key, cause };
          if (cause === 'popup' && picked) {
            usedLookup = true;
            extra.lookupId = picked.id;
            extra.typed = picked.typed || typed[leaf] || '';
            extra.query = picked.query;
            if (picked.cancelled) extra.cancelled = true;
          } else if (search) {
            extra.typed = typed[leaf] ?? '';
          }
          if (search || cause === 'popup') {
            const name = pairedNameOf(key, now);
            if (name !== undefined) extra.name = name;
          }
          delete typed[leaf];
          record({
            kind: 'field', target: leaf, value, extra,
          });
          return;
        }
        if (anchor) {
          lastField[key] = value;
          lastLeaf[leaf] = value;
        }
        // 화면이 채운 값. 버리지 않고 원인과 함께 한 줄로 묶는다 — 기본값·연쇄 채움 분석에 쓴다.
        if (filledCount >= STATE_MAX_KEYS) return;
        const after = change.after === undefined ? null : change.after;
        filled[key] = typeof after === 'string' ? after.slice(0, STATE_MAX_VALUE) : after;
        filledCount += 1;
      });
      // 상태에 없는 칸(공유 컴포넌트가 제 상태에 둔 것)은 DOM 에서 본 값으로 남긴다.
      Object.keys(domOnly).forEach((key) => {
        const value = domOnly[key];
        delete domOnly[key];
        if (lastLeaf[key] === value) return;
        lastLeaf[key] = value;
        record({
          kind: 'field', target: key, value, extra: { source: 'dom', cause: 'input', path: key },
        });
      });
      if (filledCount) {
        record({
          kind: 'state',
          target: '',
          value: String(filledCount),
          extra: {
            changed: filled,
            cause: picked ? 'popup' : fallback,
            lookupId: picked ? picked.id : undefined,
            action: trigger.action,
          },
        });
      }
      if (picked && usedLookup) lookup = null;
    } catch {
      // 비교 실패는 버린다.
    }
  };

  // --- 표 칸 -----------------------------------------------------------------

  const flushCells = () => {
    if (cellTimer) {
      clearTimeout(cellTimer);
      cellTimer = null;
    }
    const pending = pendingCells;
    pendingCells = {};
    if (!active) return;
    Object.keys(pending).forEach((key) => {
      try {
        const {
          commit: raw, route, actor, ts,
        } = pending[key];
        const commit = settledCell(raw);
        if (!commit || isSecretKey(commit.column)) return;
        const keys = Object.keys(commit.changes);
        const main = commit.changes[commit.column] ? commit.column : keys[0];
        const one = commit.changes[main];
        const extra: Record<string, unknown> = {
          row: commit.row,
          rowKey: commit.rowKey,
          label: commit.label,
          before: one.before,
          source: commit.source,
          lookupId: commit.lookupId,
        };
        if (keys.length > 1 || main !== commit.column) {
          const changes: Record<string, unknown> = {};
          keys.forEach((name) => { changes[name] = commit.changes[name].after; });
          extra.changes = changes;
        }
        if (isCheckValue(one.after) && (isCheckValue(one.before) || one.before === null)) {
          extra.checked = isChecked(one.after);
        }
        pushEvent({
          kind: 'cell', target: `${commit.grid}.${main}`, value: one.after, extra,
        }, { route, actor, now: ts });
      } catch {
        // 한 칸이 실패해도 나머지는 낸다.
      }
    });
  };

  const addCell = (commit: CellCommit) => {
    if (!active) return;
    const key = cellKey(commit.grid, commit.row, commit.column);
    const prev = pendingCells[key];
    pendingCells[key] = {
      commit: mergeCell(prev?.commit, commit),
      route: prev?.route ?? currentRoute(),
      actor: actorOf(agentBusy()),
      ts: prev?.ts ?? Date.now(),
    };
    if (cellTimer) clearTimeout(cellTimer);
    cellTimer = setTimeout(flushCells, CELL_SETTLE_MS);
  };

  /** 화면 위 그 행의 글자. 표 이름이 없거나 행이 스크롤 밖이면 빈 글자. */
  const rowLabelOf = (grid: string, row: string): string | undefined => {
    try {
      const scope = grid ? document.querySelector(`[data-el-grid="${CSS.escape(grid)}"]`) : null;
      const el = scope?.querySelector(`[data-el-row="${CSS.escape(row)}"]`);
      return el ? rowText(el) : undefined;
    } catch {
      return undefined;
    }
  };

  /** 부품 신호 — `TableLayout.rowUpdate` 가 확정한 칸 변경. */
  const onCellSignal = (event: Event) => {
    if (!active) return;
    try {
      const detail = (event as CustomEvent).detail as {
        grid?: string; index?: number; column?: string; before?: unknown; after?: unknown; changes?: Record<string, unknown>;
      } | null;
      if (!detail || !detail.changes) return;
      const grid = String(detail.grid ?? '');
      const row = String(detail.index ?? '');
      const column = String(detail.column ?? '') || Object.keys(detail.changes)[0] || '';
      const now = Date.now();
      const key = cellKey(grid, row, column);
      hookAt[key] = now;
      const before = (detail.before && typeof detail.before === 'object' ? detail.before : {}) as Record<string, unknown>;
      const changes: CellCommit['changes'] = {};
      Object.keys(detail.changes).forEach((name) => {
        if (isSecretKey(name)) return;
        changes[name] = { before: toScalar(before[name]), after: toScalar(detail.changes?.[name]) };
      });
      const recent = lookup && lookup.doneAt !== undefined && !lookup.cancelled ? lookup : null;
      const source = cellSource({
        now, cell: key, gesture, lookupAt: recent?.doneAt,
      });
      addCell({
        grid,
        row,
        column,
        changes,
        source,
        hook: true,
        rowKey: rowIdentity(detail.after ?? detail.before, Object.keys(detail.changes)),
        label: rowLabelOf(grid, row),
        lookupId: source === 'popup' ? recent?.id : undefined,
      });
    } catch {
      // 무시
    }
  };

  /**
   * 표 밖 부품(GridLayout 등)의 체크칸. 누른 뒤 켜짐이 실제로 바뀌었고 부품 신호가 안 왔으면
   * DOM 에서 본 것으로 낸다. 경고창에 막혀 안 바뀌었으면 아무것도 안 남긴다.
   */
  const watchCheck = (box: Element, place: { grid: string; row: string; column: string; rowEl: Element }, at: number) => {
    const before = box.getAttribute('aria-checked');
    setTimeout(() => {
      try {
        const key = cellKey(place.grid, place.row, place.column);
        if ((hookAt[key] ?? 0) >= at || !box.isConnected) return;
        const after = box.getAttribute('aria-checked');
        if (after === null || after === before) return;
        addCell({
          grid: place.grid,
          row: place.row,
          column: place.column,
          changes: { [place.column]: { before: before === 'true' ? '1' : '0', after: after === 'true' ? '1' : '0' } },
          source: 'check',
          hook: false,
          label: rowText(place.rowEl),
        });
      } catch {
        // 무시
      }
    }, CHECK_VERIFY_MS);
  };

  // --- 팝업 -------------------------------------------------------------------

  /** 부품 신호 — 공용 팝업(ModalStore)의 열기·검색·고르기·닫기. */
  const onLookupSignal = (event: Event) => {
    if (!active) return;
    try {
      const detail = (event as CustomEvent).detail as {
        step?: string; popup?: string; query?: string; item?: unknown; auto?: boolean;
      } | null;
      if (!detail) return;
      const now = Date.now();
      const popup = String(detail.popup ?? '');
      if (detail.step === 'open') {
        const focusedEl = document.activeElement;
        // 표 칸에서 연 팝업은 칸이 아니라 `cell` 의 출처(popup)로 이어진다.
        const inCell = Boolean(focusedEl && focusedEl.closest && cellPlace(focusedEl));
        const focused = focusedEl && focusedEl.closest && !inCell ? fieldKeyOf(focusedEl) : null;
        const recentField = lastSearchField && now - lastSearchField.at < 10000 ? lastSearchField.key : undefined;
        const field = focused ?? (inCell ? undefined : recentField);
        lookupCount += 1;
        const query = String(detail.query ?? '');
        lookup = {
          id: `${session.slice(0, 8)}-${seq}-${lookupCount}`,
          popup,
          field,
          typed: query || (field ? typed[field] ?? '' : ''),
          query,
          values: [],
        };
        record({
          kind: 'lookup',
          target: popup,
          value: query,
          extra: {
            step: 'open', lookupId: lookup.id, field, typed: lookup.typed,
          },
        });
        return;
      }
      if (!lookup || lookup.popup !== popup || lookup.doneAt !== undefined) return;
      if (detail.step === 'query') {
        const query = String(detail.query ?? '');
        if (query === lookup.query) return;
        lookup.query = query;
        record({
          kind: 'lookup', target: popup, value: query, extra: { step: 'query', lookupId: lookup.id, field: lookup.field },
        });
        return;
      }
      if (detail.step === 'pick') {
        if (lookup.field) delete touchedFields[lookup.field];
        lookup.item = lookupItem(detail.item);
        lookup.values = lookupValues(detail.item);
        lookup.doneAt = now;
        record({
          kind: 'lookup',
          target: popup,
          value: lookup.query,
          extra: {
            step: 'pick',
            lookupId: lookup.id,
            field: lookup.field,
            typed: lookup.typed,
            picked: lookup.item,
            auto: detail.auto || undefined,
          },
        });
        touched({ kind: 'popup' });
        return;
      }
      if (detail.step === 'cancel') {
        lookup.doneAt = now;
        lookup.cancelled = true;
        record({
          kind: 'lookup', target: popup, value: lookup.query, extra: { step: 'cancel', lookupId: lookup.id, field: lookup.field },
        });
        touched({ kind: 'popup' });
      }
    } catch {
      // 무시
    }
  };

  // --- DOM 리스너 ---------------------------------------------------------------

  const onClick = (event: Event) => {
    if (!active) return;
    try {
      const target = event.target as Element | null;
      if (!target || !target.closest) return;
      const now = Date.now();
      const dialogButton = target.closest('.swal2-confirm, .swal2-cancel, .swal2-deny, .swal2-close');
      if (dialogButton) {
        answerNotice(dialogButton.classList.contains('swal2-confirm') ? 'confirm'
          : dialogButton.classList.contains('swal2-cancel') ? 'cancel'
            : dialogButton.classList.contains('swal2-deny') ? 'deny' : 'close');
        // 「저장할까요?」에 답하면 저장이 돌고 칸이 채워진다. 사람이 친 값이 아니다.
        touched({ kind: 'dialog', action: 'NOTICE' });
        return;
      }
      const action = target.closest('[data-el-action]');
      if (action) {
        const id = action.getAttribute('data-el-action') ?? '';
        record({ kind: 'action', target: id, value: text(action, 60) });
        touched({ kind: 'action', action: id });
        return;
      }
      const tab = target.closest('[data-el-tab]');
      if (tab) {
        record({ kind: 'tab', target: tab.getAttribute('data-el-tab') });
        touched({ kind: 'nav' });
        return;
      }
      const menu = target.closest('[data-el-menu]');
      if (menu) {
        record({ kind: 'menu', target: menu.getAttribute('data-el-menu'), value: text(menu, 60) });
        touched({ kind: 'nav' });
        return;
      }
      const row = target.closest('[data-el-row]');
      if (row) {
        const place = cellPlace(target);
        const box = target.closest('[role=check-box]');
        if (box && place && event.type === 'click') {
          gesture = { kind: 'check', at: now, cell: cellKey(place.grid, place.row, place.column) };
          watchCheck(box, place, now);
        } else {
          gesture = { kind: 'pointer', at: now };
        }
        const grid = row.closest('[data-el-grid]')?.getAttribute('data-el-grid') ?? '';
        const index = row.getAttribute('data-el-row') ?? '';
        const cell = place?.column;
        const dialog = dialogOf(row);
        const extra: Record<string, unknown> = { label: rowText(row), cell };
        if (dialog) {
          extra.popup = true;
          if (lookup && lookup.doneAt === undefined && !lookup.dialog) {
            extra.lookupId = lookup.id;
          } else {
            // 공용 팝업 신호가 없는 창. 이 행을 고르고 창이 닫히면 팝업 선택으로 본다 —
            // 목록 표의 행이 가상 스크롤로 DOM 에서 빠지는 것은 선택이 아니다.
            if (!lookup || lookup.dialog !== dialog || lookup.doneAt !== undefined) {
              const field = lastSearchField && now - lastSearchField.at < 30000 ? lastSearchField.key : undefined;
              lookupCount += 1;
              lookup = {
                id: `${session.slice(0, 8)}-${seq}-${lookupCount}`,
                popup: 'dialog',
                field,
                typed: field ? typed[field] ?? '' : '',
                query: '',
                values: [],
                dialog,
              };
            }
            lookup.item = { label: rowText(row) };
            lookup.values = rowCells(row);
            extra.lookupId = lookup.id;
          }
        }
        // 같은 행의 칸을 여러 번 눌러도 행 사건은 한 번이다.
        const rowKey = `${grid}\u0001${dialog ? 'dialog' : ''}`;
        if (lastRow[rowKey] !== index || event.type === 'dblclick') {
          lastRow[rowKey] = index;
          if (event.type === 'dblclick') extra.double = true;
          record({
            kind: 'row', target: grid, value: index, extra,
          });
        }
        // 팝업 안의 행 선택은 화면 칸을 바꾸지 않는다 — 원인을 「행 선택」으로 바꾸지 않는다.
        // 체크칸을 누른 뒤의 화면 변화(합계 등)는 계산이다 — 행 선택으로 적지 않는다.
        let next: Trigger | undefined = { kind: 'row' };
        if (dialog) next = undefined;
        else if (box) next = { kind: 'action', action: 'CHECK' };
        touched(next);
        return;
      }
      gesture = { kind: 'pointer', at: now };
      const field = fieldKeyOf(target);
      if (field) {
        touchField(field, now);
        touched({ kind: 'input' });
        return;
      }
      // 앵커 없는 버튼. 팝업의 「확인」·「선택」, 상세의 「추가」 같은 것 — CS 가 「무엇을 눌렀나」를 본다.
      const button = target.closest('button, [role=button]');
      if (button && event.type === 'click') {
        const label = text(button, 40);
        if (label) record({ kind: 'click', target: label });
        // 팝업 안의 단추(확인·닫기)는 팝업 신호가 원인을 정한다.
        touched(dialogOf(button) ? undefined : { kind: 'action', action: label });
        return;
      }
      touched();
    } catch {
      // 기록 실패는 조용히 버린다.
    }
  };

  /** 칸 값이 확정됐다(change). 표 안이면 `cell`, 아니면 조건 칸. 파일 칸은 이름·크기만. */
  const onChange = (event: Event) => {
    if (!active) return;
    try {
      const input = event.target as HTMLInputElement | null;
      if (!input || !input.closest || isPasswordInput(input)) return;
      const nameAttr = input.getAttribute('name') ?? input.id ?? '';
      if (isSecretKey(nameAttr)) return;
      const now = Date.now();
      if (input instanceof HTMLInputElement && input.type === 'file') {
        const files = Array.from(input.files ?? []).map((file) => ({ name: file.name, size: file.size, type: file.type }));
        const key = input.closest('[data-el-field]')?.getAttribute('data-el-field') ?? nameAttr;
        record({
          kind: 'file', target: key, value: files.map((file) => file.name).join(', '), extra: { files },
        });
        touched({ kind: 'input' });
        return;
      }
      const place = cellPlace(input);
      if (place) {
        if (isSecretKey(place.column)) return;
        gesture = { kind: 'input', at: now, cell: cellKey(place.grid, place.row, place.column) };
        // 부품 신호가 같은 칸을 알리면 합쳐진다(신호 쪽 이전 값·행 키가 이긴다).
        addCell({
          grid: place.grid,
          row: place.row,
          column: place.column,
          changes: { [place.column]: { before: null, after: valueOf(input) } },
          source: 'input',
          hook: false,
          label: rowText(place.rowEl),
        });
        touched({ kind: 'input' });
        return;
      }
      const key = fieldKeyOf(input);
      if (key) {
        domOnly[key] = valueOf(input);
        touchField(key, now);
      }
      touched({ kind: 'input' });
    } catch {
      // 기록 실패는 조용히 버린다.
    }
  };

  /** 친 글자를 기억한다. 사건은 남기지 않는다 — 값이 확정될 때 `field`·`cell`·`lookup` 에 싣는다. */
  const onInput = (event: Event) => {
    if (!active) return;
    try {
      const input = event.target as HTMLInputElement | null;
      if (!input || !input.closest || isPasswordInput(input)) return;
      const now = Date.now();
      const place = cellPlace(input);
      if (place) {
        gesture = { kind: 'input', at: now, cell: cellKey(place.grid, place.row, place.column) };
        return;
      }
      // 공용 팝업 신호가 없는 창의 검색어.
      if (lookup && lookup.dialog && lookup.doneAt === undefined && dialogOf(input)) {
        lookup.query = String(input.value ?? '');
      }
      const key = fieldKeyOf(input);
      if (!key) return;
      touchField(key, now);
      const field = input.closest('[data-el-field]');
      if (field?.hasAttribute('data-el-search') || input.hasAttribute('data-el-search')) typed[key] = String(input.value ?? '');
    } catch {
      // 무시
    }
  };

  const onKey = (event: KeyboardEvent) => {
    if (!active) return;
    try {
      const target = event.target as Element | null;
      const now = Date.now();
      if (target && target.closest) {
        const place = cellPlace(target);
        gesture = place
          ? { kind: 'input', at: now, cell: cellKey(place.grid, place.row, place.column) }
          : { kind: 'key', at: now };
        if (!place) touchField(fieldKeyOf(target), now);
      }
      // Enter·Tab 은 값 확정·팝업 열기의 방아쇠다. 글자 하나하나로 비교를 돌리지 않는다.
      if (event.key === 'Enter' || event.key === 'Tab' || event.key === 'Escape') touched({ kind: 'input' });
    } catch {
      // 무시
    }
  };

  // --- 알림창(sweetalert2) ---------------------------------------------------
  type Notice = {
    ts: number; actor: 'human' | 'agent'; route: string; value: string; type: string; answered: boolean;
    /** 떴을 때 마지막으로 낸 순번. 알림은 답한 뒤에 한 줄로 남으므로 인과 자리를 이것으로 편다. */
    afterSeq: number;
  };
  let notice: Notice | null = null;

  const readNotice = (container: Element): Notice | null => {
    const title = text(container.querySelector('.swal2-title'), 500);
    const body = text(container.querySelector('.swal2-html-container, .swal2-content'), 2000);
    if (!title && !body) return null;
    const cancel = container.querySelector<HTMLElement>('.swal2-cancel');
    let confirm = false;
    try {
      confirm = Boolean(cancel) && window.getComputedStyle(cancel as HTMLElement).display !== 'none';
    } catch {
      confirm = false;
    }
    return {
      ts: Date.now(),
      actor: actorOf(agentBusy()),
      route: currentRoute(),
      value: [title, body].filter(Boolean).join(' — '),
      type: confirm ? 'confirm' : 'alert',
      answered: false,
      afterSeq: seq - 1,
    };
  };

  /**
   * 알림은 답이 나온 뒤에 한 줄로 남긴다. `ts` 는 떴을 때(호환), 순번은 답한 때다.
   * 떴을 때·답한 때(`shownAt`·`answeredAt`)와 떴을 때 직전 순번(`afterSeq`)을 함께 싣는다 —
   * 첫 배포에서 알림이 뒤늦게 들어와 id·시각 순서가 57곳 뒤집혔다.
   */
  const emitNotice = (answer: string) => {
    if (!notice || notice.answered || !active) return;
    notice.answered = true;
    const answeredAt = Date.now();
    pushEvent(
      {
        kind: 'notice',
        value: notice.value,
        ts: notice.ts,
        extra: {
          type: notice.type,
          answer,
          ms: answeredAt - notice.ts,
          shownAt: notice.ts,
          answeredAt,
          afterSeq: notice.afterSeq,
        },
      },
      { route: notice.route, actor: notice.actor, now: notice.ts },
    );
  };

  const answerNotice = (answer: string) => emitNotice(answer);

  const watchDialogs = () => {
    let inner: MutationObserver | null = null;
    const seen = (container: Element) => {
      // 내용은 붙은 직후에 채워질 수 있다. 한 틱 미룬다.
      setTimeout(() => {
        try {
          const read = readNotice(container);
          if (read) notice = read;
        } catch {
          // 무시
        }
      }, 0);
      // 같은 창을 다시 써서 다음 알림을 띄우는 경우 — 답한 뒤 글이 바뀌면 새 알림이다.
      try {
        inner?.disconnect();
        inner = new MutationObserver(() => {
          try {
            if (!notice || !notice.answered) return;
            const read = readNotice(container);
            if (read && read.value !== notice.value) notice = read;
          } catch {
            // 무시
          }
        });
        inner.observe(container, { childList: true, subtree: true, characterData: true });
      } catch {
        inner = null;
      }
    };
    try {
      new MutationObserver((mutations) => {
        if (!active) return;
        try {
          mutations.forEach((mutation) => {
            Array.from(mutation.addedNodes).forEach((node) => {
              if (node instanceof Element && node.classList.contains('swal2-container')) seen(node);
            });
            Array.from(mutation.removedNodes).forEach((node) => {
              if (node instanceof Element && node.classList.contains('swal2-container')) {
                inner?.disconnect();
                inner = null;
                emitNotice('closed');
                notice = null;
              }
            });
          });
        } catch {
          // 무시
        }
      }).observe(document.body, { childList: true });
    } catch {
      // MutationObserver 가 없으면 알림을 못 남길 뿐이다.
    }
  };

  // --- 보내기 ---------------------------------------------------------------
  const post = (body: string, keepalive: boolean) => fetch(`${serviceHost()}${EVENTS}`, {
    method: 'POST',
    // text/plain 이면 CORS 사전 요청(OPTIONS)이 없다 — 5초마다 요청이 두 번씩 가지 않는다.
    // 서버(gin `ShouldBindJSON`, 권한 미들웨어)는 헤더를 보지 않고 본문을 JSON 으로 푼다.
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body,
    keepalive,
    credentials: 'omit',
  });

  // 보내기에 실패하면 잠시 쉰다 — 서버가 죽었거나 로그인이 끊긴 동안 5초마다 두드리지 않게.
  let resumeAt = 0;
  const RETRY_MS = 30_000;

  function flush() {
    if (inFlight || !buffer.size || Date.now() < resumeAt) return;
    let who = null;
    try {
      who = active ? caller() : null;
    } catch {
      who = null;
    }
    if (!who) {
      buffer.clear();
      return;
    }
    // keepalive 는 64KB 까지만 받는다. 작은 덩이면 keepalive 로, 크면 보통 요청으로.
    const events = buffer.take(MAX_BATCH, NORMAL_BYTES);
    const body = batchBody(who, session, events);
    inFlight = true;
    // **서버가 받지 않았으면 되돌려 놓는다.** 전에는 네트워크 오류만 삼키고 401·5xx 응답도 성공으로 쳐서
    // 그 덩이를 버렸다 — 숨겨 둔 탭이 한 시간 뒤 돌아와 보낸 덩이가 그렇게 사라졌다(2차 연구 B-2).
    const failed = () => {
      buffer.putBack(events);
      resumeAt = Date.now() + RETRY_MS;
    };
    try {
      post(body, body.length * 3 < KEEPALIVE_BYTES)
        .then((response) => { if (!response.ok) failed(); })
        .catch(failed)
        .then(() => { inFlight = false; });
    } catch {
      failed();
      inFlight = false;
    }
  }

  /** 탭을 닫거나 숨길 때. 남은 것을 전부 비콘으로 — 64KB 씩 나눠서. */
  const flushOnHide = () => {
    if (!active) return;
    try {
      flushCells();
    } catch {
      // 무시
    }
    if (!buffer.size) return;
    let who = null;
    try {
      who = caller();
    } catch {
      who = null;
    }
    if (!who) return;
    const url = `${serviceHost()}${EVENTS}`;
    for (let guard = 0; buffer.size && guard < 40; guard += 1) {
      const events = buffer.take(MAX_BATCH, KEEPALIVE_BYTES / 3);
      const body = batchBody(who, session, events);
      try {
        // text/plain Blob 이면 비콘이 사전 요청 없이 간다(위 post 와 같은 이유).
        const sent = typeof navigator.sendBeacon === 'function'
          && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        if (!sent) post(body, true).catch(() => undefined);
      } catch {
        // 버린다.
      }
    }
  };

  // --- 오류 -----------------------------------------------------------------
  const onError = (event: ErrorEvent) => {
    if (!active) return;
    try {
      const message = String(event.message || event.error?.message || '');
      if (!message || !errors.allow(message, Date.now())) return;
      record({
        kind: 'error',
        target: 'error',
        value: message,
        extra: { stack: String(event.error?.stack ?? ''), source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined },
      });
    } catch {
      // 무시
    }
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (!active) return;
    try {
      const reason = event.reason as { message?: string; stack?: string } | string | undefined;
      const message = typeof reason === 'string' ? reason : String(reason?.message ?? reason ?? '');
      if (!message || !errors.allow(message, Date.now())) return;
      record({
        kind: 'error',
        target: 'unhandledrejection',
        value: message,
        extra: { stack: typeof reason === 'object' ? String(reason?.stack ?? '') : '' },
      });
    } catch {
      // 무시
    }
  };

  /**
   * 부품 신호 — DOM change 없이 확정된 값(달력·Enter 로 고른 날짜). 표 안이면 표 칸, 밖이면
   * 앵커·라벨 이름의 칸으로 남긴다. 점검계획 상세의 점검일자 옮기기가 이 길로만 보인다.
   */
  const onValueSignal = (event: Event) => {
    if (!active) return;
    try {
      const detail = (event as CustomEvent).detail as { element?: Element | null; value?: unknown } | null;
      const element = detail?.element;
      if (!element || !element.closest) return;
      const value = String(detail?.value ?? '');
      const now = Date.now();
      const place = cellPlace(element);
      if (place) {
        if (isSecretKey(place.column)) return;
        gesture = { kind: 'input', at: now, cell: cellKey(place.grid, place.row, place.column) };
        addCell({
          grid: place.grid,
          row: place.row,
          column: place.column,
          changes: { [place.column]: { before: null, after: value } },
          source: 'input',
          hook: false,
          label: rowText(place.rowEl),
        });
        touched({ kind: 'input' });
        return;
      }
      const key = fieldKeyOf(element);
      if (!key) return;
      domOnly[key] = value;
      touchField(key, now);
      touched({ kind: 'input' });
    } catch {
      // 무시
    }
  };

  // --- 걸기 -----------------------------------------------------------------
  const options = { capture: true, passive: true } as AddEventListenerOptions;
  document.addEventListener('click', onClick, options);
  document.addEventListener('dblclick', onClick, options);
  document.addEventListener('change', onChange, options);
  document.addEventListener('input', onInput, options);
  document.addEventListener('keydown', onKey as EventListener, options);
  window.addEventListener('elman:cell', onCellSignal, { passive: true });
  window.addEventListener('elman:lookup', onLookupSignal, { passive: true });
  window.addEventListener('elman:value', onValueSignal, { passive: true });
  window.addEventListener('error', onError as EventListener, options);
  window.addEventListener('unhandledrejection', onRejection as EventListener, options);
  window.addEventListener('pagehide', flushOnHide, options);
  document.addEventListener('visibilitychange', () => {
    try {
      if (document.visibilityState === 'hidden') {
        record({ kind: 'visibility', value: 'hidden' });
        flushOnHide();
      } else {
        record({ kind: 'visibility', value: 'visible' });
        // 돌아오면 쌓인 것(숨김 때 못 보낸 것 포함)을 곧바로 보낸다 — 다음 5초를 기다리다 탭이 또 닫힌다.
        resumeAt = 0;
        flush();
      }
    } catch {
      // 무시
    }
  }, options);
  watchDialogs();

  // 1초마다 로그인·끄기 여부와 화면 전환을 본다. 보내기는 5틱마다.
  refreshActive();
  setInterval(() => {
    try {
      refreshActive();
      if (!active) return;
      checkRoute();
      // 화면이 열리며 스스로 채우는 값(콤보 목록·기본 조건·자동 조회)은 사람이 한 일이 아니다.
      if (pristine) baseline = snapshot();
      ticks += 1;
      if (ticks % FLUSH_TICKS === 0) flush();
    } catch {
      // 무시
    }
  }, TICK_MS);
}

/** 행의 칸 글자들. 칸 앵커가 없으면 빈 배열. */
function rowCells(row: Element): string[] {
  const out: string[] = [];
  const cells = row.querySelectorAll('[data-el-cell]');
  for (let i = 0; i < cells.length && out.length < 12; i += 1) {
    const one = rowLabel(cells[i].textContent, 60);
    if (one.length >= 2) out.push(one);
  }
  return out;
}

/**
 * 행을 사람이 읽을 글자로. 칸 앵커가 있으면 칸마다 ` | ` 로 나눈다 — `textContent` 를 통째로
 * 쓰면 칸 경계가 사라져 `00000더 브라운 캐슬565-08-01889` 처럼 붙는다.
 */
function rowText(row: Element): string {
  const cells = row.querySelectorAll('[data-el-cell]');
  if (!cells.length) return rowLabel(row.textContent);
  const parts: string[] = [];
  for (let i = 0; i < cells.length; i += 1) {
    const one = rowLabel(cells[i].textContent, 60);
    if (one) parts.push(one);
  }
  return rowLabel(parts.join(' | '));
}

/** 입력칸의 값. 체크칸은 켜짐/꺼짐, 고르기칸은 고른 글자까지. */
function valueOf(input: Element): string {
  if (input instanceof HTMLInputElement) {
    if (input.type === 'checkbox' || input.type === 'radio') return input.checked ? '1' : '0';
    return String(input.value ?? '');
  }
  if (input instanceof HTMLSelectElement) return String(input.value ?? '');
  if (input instanceof HTMLTextAreaElement) return String(input.value ?? '');
  return rowLabel(input.textContent);
}
